import { marked } from "marked";
import DOMPurify from "dompurify";

/**
 * Renders agent markdown to safe HTML with the three TradeParse semantic
 * conventions (spec: productionize-agent.md §"Agent response rich formatting"):
 *
 * 1. {{pnl:+40007.11}} → colored caret span (▲ green / ▼ red, tabular-nums)
 * 2. [label](app:/portfolio?asof=...) → in-app link chip. Whitelisted routes/
 *    params only, ISO dates enforced, pid dropped, non-app links stripped to
 *    plain text. Chips carry data attributes; navigation happens via event
 *    delegation in the message component (no live hrefs in the HTML).
 * 3. ```stats fenced JSON → stat-grid card. Malformed JSON → plain code block.
 *
 * Everything degrades gracefully: unknown tokens render as text.
 */

const SHARED_PORTFOLIO_PARAMS = ["start", "end", "scenario"] as const;
const APP_ROUTE_PARAMS = new Map<string, ReadonlySet<string>>([
  ["/experiments", new Set()],
  ["/portfolios", new Set()],
  ["/dashboard", new Set(["start", "end"])],
  ["/portfolio", new Set(["asof", "view", ...SHARED_PORTFOLIO_PARAMS])],
  ["/portfolio/trends", new Set(["chart", "symbol", "layout", "yscale", ...SHARED_PORTFOLIO_PARAMS])],
  ["/portfolio/summary", new Set(["view", "filter", "summaryView", "premiumUnderlying", "premiumType", "premiumSection", "premiumCadence", "premiumActivity", ...SHARED_PORTFOLIO_PARAMS])],
  ["/portfolio/efficiency", new Set(["premiumUnderlying", "premiumType", "premiumSection", "premiumCadence", "premiumActivity", ...SHARED_PORTFOLIO_PARAMS])],
  ["/portfolio/experiments", new Set(SHARED_PORTFOLIO_PARAMS)],
  ["/history", new Set(["start", "end"])],
  ["/ai-assistant", new Set()],
  ["/settings/general", new Set()],
  ["/portfolio/notes", new Set()],
  ["/portfolio/memory", new Set()],
  ["/portfolio/automations", new Set()],
  ["/portfolio/alerts", new Set()],
  ["/portfolio/schedules", new Set()],
  ["/settings/chat-accounts", new Set()],
  ["/walkthroughs", new Set()],
]);
const DATE_PARAMS = new Set(["asof", "start", "end"]);
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
// Whitespace-tolerant: weaker models occasionally emit "{{ pnl: +5.10 }}".
// Thousands commas tolerated (a mid-tier model wrote {{pnl:+44,361}}; the old
// pattern didn't match it, so the LITERAL token text reached the user).
const PNL_TOKEN = /\{\{\s*pnl:\s*([+-]?\d[\d,]*(?:\.\d+)?)\s*\}\}/g;

// Assistant-derived figures. {{pnl:}} asserts "TradeParse computed this";
// {{calc:}} asserts "Vega worked this out". The payload is free-form
// (a dollar figure, a percent, a rate like "~$190/day") because policing its
// format would just push the model into emitting plain text instead — and an
// unmarked derived number is the failure this exists to prevent.
const CALC_TOKEN = /\{\{\s*calc:\s*([^}]{1,60}?)\s*\}\}/g;

marked.setOptions({ gfm: true, breaks: true });

// Strikethrough is disabled outright. GFM pairs SINGLE tildes, and the agent
// uses "~" to mean "approximately" — observed 2026-08-09: "{{calc:~2.49
// years}} → about {{calc:~$13,250/year}}" had its two tildes paired into one
// <del> spanning both tokens, which struck through the text AND split the
// tokens across element boundaries so neither became a calc span. A struck-out
// figure reads as "wrong/deleted" — worse than losing strikethrough, which the
// agent never legitimately needs. Returning undefined (not false, which would
// fall back to the original tokenizer) makes the del rule never match; code
// fences were never affected (inline rules don't run there) and "~" renders
// literally everywhere.
marked.use({ tokenizer: { del: () => undefined } });

export function renderAgentMarkdown(raw: string): string {
  const pieces: string[] = [];
  for (const segment of splitFences(raw)) {
    if (segment.kind === "stats") {
      pieces.push(renderStatsCard(segment.body));
    } else if (segment.kind === "sources") {
      pieces.push(renderSourcesCard(segment.body));
    } else {
      pieces.push(renderMarkdownSegment(segment.body));
    }
  }
  return pieces.join("");
}

/**
 * Streaming-safe variant: hide structure that is still arriving instead of
 * flashing it raw. Mid-stream, an unclosed ``` fence renders as its literal
 * JSON/code and an unclosed {{ token as literal braces — then both SNAP into
 * their formatted shapes a moment later, shifting the layout under the
 * reader (user-reported 2026-08-09). The runtime already holds most of this
 * back server-side (splitAtOpenToken, fence-parity hold); this is the
 * client's own guard so no transport or edge case can flash raw markup.
 *
 * The held tail is replaced by a small pulsing placeholder so the reader
 * sees "something is coming" rather than a snap-in. Detection mirrors the
 * server hold: fence PARITY (odd count of ``` = a fence is open), an
 * unclosed {{ token, and a trailing 1–2 backtick run that may be a split
 * fence marker.
 */
export function renderAgentMarkdownStreaming(raw: string): string {
  let holdFrom = raw.length;
  const lastOpen = raw.lastIndexOf("{{");
  if (lastOpen !== -1 && raw.indexOf("}}", lastOpen) === -1) {
    holdFrom = Math.min(holdFrom, lastOpen);
  }
  const fenceMarkers = [...raw.matchAll(/```/g)];
  if (fenceMarkers.length % 2 === 1) {
    holdFrom = Math.min(holdFrom, fenceMarkers[fenceMarkers.length - 1].index ?? raw.length);
  }
  const trailingTicks = raw.match(/`+$/)?.[0].length ?? 0;
  if (trailingTicks === 1 || trailingTicks === 2) {
    holdFrom = Math.min(holdFrom, raw.length - trailingTicks);
  }
  if (holdFrom >= raw.length) return renderAgentMarkdown(raw);
  return (
    renderAgentMarkdown(raw.slice(0, holdFrom)) +
    '<div class="tp-pending" aria-label="More content arriving"><span></span><span></span><span></span></div>'
  );
}

type Segment = { kind: "md" | "stats" | "sources"; body: string };

function splitFences(raw: string): Segment[] {
  const segments: Segment[] = [];
  // stats = the app's own numbers; sources = material from the web. Rendered
  // differently on purpose: a reader must be able to tell at a glance which
  // parts of an answer came from their reconstructed book and which came from
  // outside it.
  const fence = /```(stats|sources)\s*\n([\s\S]*?)```/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = fence.exec(raw)) !== null) {
    if (m.index > last) segments.push({ kind: "md", body: raw.slice(last, m.index) });
    segments.push({ kind: m[1] as "stats" | "sources", body: m[2] });
    last = m.index + m[0].length;
  }
  if (last < raw.length) segments.push({ kind: "md", body: raw.slice(last) });
  return segments;
}

function renderMarkdownSegment(md: string): string {
  // Defang calc payloads BEFORE parsing: markup-like characters inside a
  // token would otherwise be parsed into real elements by marked, splitting
  // the token across DOM nodes so the substitution pass can never neutralize
  // it ({{calc:<img>}} became an actual <img>). Entities keep the payload a
  // single text node; the DOM pass then escapes it into the span. (Corner
  // accepted: a calc payload containing <, >, or & inside a CODE FENCE shows
  // its entity form — fences are verbatim.)
  const defanged = md.replace(
    CALC_TOKEN,
    (_all, text: string) =>
      `{{calc:${text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}}}`,
  );
  const html = marked.parse(defanged, { async: false }) as string;
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_URI_REGEXP: /^(?:app:|https?:)/i,
    ADD_ATTR: ["target"],
  });
  return wrapTables(transformAnchors(substituteTokens(clean)));
}

/**
 * Post-parse token pass: {{pnl:}} / {{calc:}} become styled spans — but ONLY
 * outside code contexts.
 *
 * Tokens used to be substituted into the RAW MARKDOWN before parsing, which
 * worked everywhere except one place: a token inside a code fence. The span
 * HTML was injected into the fence's content, and code blocks display their
 * content literally — so the user saw raw `<span class="tp-calc" …>` markup
 * in a beige box (observed 2026-08-08: a model drew an ASCII P/L timeline in
 * a fence and calc-marked a figure inside it). Walking the parsed DOM's text
 * nodes fixes the class: inside `pre`/`code` the token collapses to its plain
 * text (a diagram stays a diagram), everywhere else it becomes the span.
 */
function substituteTokens(html: string): string {
  const template = document.createElement("template");
  template.innerHTML = html;
  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if ((n.nodeValue ?? "").includes("{{")) textNodes.push(n as Text);
  }
  for (const node of textNodes) {
    const raw = node.nodeValue ?? "";
    if (node.parentElement?.closest("pre, code")) {
      // Code context: plain text only — never inject markup into content that
      // renders literally. ▲/▼ carets survive as characters.
      node.nodeValue = raw
        .replace(PNL_TOKEN, (_all, amount: string) => pnlPlainText(amount))
        .replace(CALC_TOKEN, (_all, text: string) => text);
      continue;
    }
    // Normal prose: escape the surrounding text once, then let the token
    // builders (which emit trusted, self-constructed markup) replace in.
    // Token payloads are numeric or short free text; pnlSpan re-derives its output
    // from the parsed number, and calc payloads are already escaped here.
    const substituted = escapeHtml(raw)
      .replace(PNL_TOKEN, (_all, amount: string) => pnlSpan(amount))
      .replace(CALC_TOKEN, (_all, text: string) => calcSpanPreEscaped(text));
    if (substituted === escapeHtml(raw)) continue;
    const frag = document.createElement("template");
    frag.innerHTML = substituted;
    node.replaceWith(frag.content);
  }
  return template.innerHTML;
}

/** Text-only rendering of a pnl token for code contexts. */
function pnlPlainText(amount: string): string {
  const value = Number(amount.replace(/(\d),(?=\d{3}\b)/g, "$1"));
  if (!Number.isFinite(value)) return `{{pnl:${amount}}}`;
  const formatted = Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${value < 0 ? "▼" : "▲"} $${formatted}`;
}

/** calcSpan variant for input that is ALREADY html-escaped (the DOM pass). */
function calcSpanPreEscaped(escapedText: string): string {
  return `<span class="tp-calc" title="Calculated by Vega, not from your reconstructed data">${escapedText}</span>`;
}

/**
 * Post-sanitize pass: every <table> gets its own horizontal-scroll container.
 *
 * Without this, a wide table is squeezed into the bubble width and cells
 * inherit the bubble's `overflow-wrap: anywhere` (a guard for long URLs) —
 * which permits breaking MID-NUMBER, so "40" renders as "4" over "0". The
 * wrapper (.tp-table-wrap) scrolls sideways instead; its CSS restores normal
 * word-wrapping inside cells so columns keep an adequate width for their data
 * and values never split across lines.
 */
function wrapTables(html: string): string {
  const template = document.createElement("template");
  template.innerHTML = html;
  for (const table of Array.from(template.content.querySelectorAll("table"))) {
    // Idempotence guard — never double-wrap.
    if (table.parentElement?.classList.contains("tp-table-wrap")) continue;
    const wrap = document.createElement("div");
    wrap.className = "tp-table-wrap";
    table.replaceWith(wrap);
    wrap.appendChild(table);
  }
  return template.innerHTML;
}

/** Directional P/L caret. Renderer rounds defensively to 2dp. */
function pnlSpan(amount: string): string {
  // Strip well-formed thousands groups only ("44,361" -> 44361); anything
  // still unparseable falls through to the literal-text guard below.
  const value = Number(amount.replace(/(\d),(?=\d{3}\b)/g, "$1"));
  if (!Number.isFinite(value)) return escapeHtml(`{{pnl:${amount}}}`);
  const negative = value < 0;
  const formatted = Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `<span class="tp-pnl ${negative ? "tp-pnl--neg" : "tp-pnl--pos"}">${negative ? "▼" : "▲"}&hairsp;$${formatted}</span>`;
}

/**
 * A figure Vega worked out rather than one TradeParse computed.
 * Marked, not hidden: the point is that the reader can tell at a glance which
 * numbers carry the app's guarantee and which are Vega's reasoning.
 */
function calcSpan(text: string): string {
  return `<span class="tp-calc" title="Calculated by Vega, not from your reconstructed data">${escapeHtml(text)}</span>`;
}

/**
 * Post-sanitize anchor pass: valid app: links become inert chips with data
 * attributes; every other link (including invalid app: links) collapses to
 * its label text.
 */
function transformAnchors(html: string): string {
  const template = document.createElement("template");
  template.innerHTML = html;
  for (const anchor of Array.from(template.content.querySelectorAll("a"))) {
    const app = parseAppLink(anchor.getAttribute("href") ?? "");
    if (app) {
      const chip = document.createElement("span");
      chip.className = "tp-app-link";
      chip.setAttribute("role", "link");
      chip.setAttribute("tabindex", "0");
      chip.dataset["appPath"] = app.path;
      chip.dataset["appQuery"] = app.query;
      chip.textContent = anchor.textContent ?? "";
      anchor.replaceWith(chip);
    } else {
      anchor.replaceWith(document.createTextNode(anchor.textContent ?? ""));
    }
  }
  return template.innerHTML;
}

function parseAppLink(href: string): { path: string; query: string } | null {
  if (!href.startsWith("app:/")) return null;
  const rest = href.slice("app:".length);
  const [path, queryString = ""] = rest.split("?");
  const allowedParams = APP_ROUTE_PARAMS.get(path);
  if (!allowedParams) return null;
  const params = new URLSearchParams(queryString);
  const kept = new URLSearchParams();
  for (const [key, value] of params) {
    if (key === "pid") continue; // the app injects pid — never trust the link's
    if (!allowedParams.has(key)) return null;
    if (DATE_PARAMS.has(key) && !ISO_DATE.test(value)) return null;
    kept.set(key, value);
  }
  return { path, query: kept.toString() };
}

interface StatsPayload {
  title?: unknown;
  items?: Array<{ label?: unknown; pnl?: unknown; value?: unknown }>;
}

function renderStatsCard(body: string): string {
  let parsed: StatsPayload;
  try {
    parsed = JSON.parse(body);
    if (!parsed || !Array.isArray(parsed.items)) throw new Error("bad shape");
  } catch {
    // Malformed stats JSON → show as a plain code block, never throw.
    return `<pre class="tp-stats-fallback"><code>${escapeHtml(body)}</code></pre>`;
  }
  const title =
    typeof parsed.title === "string" && parsed.title
      ? `<div class="tp-stats-title">${escapeHtml(parsed.title)}</div>`
      : "";
  const items = parsed.items
    .map((item) => {
      const label = escapeHtml(typeof item.label === "string" ? item.label : "");
      if (typeof item.pnl === "number" && Number.isFinite(item.pnl)) {
        return `<div class="tp-stat"><span class="tp-stat-label">${label}</span><span class="tp-stat-value">${pnlSpan(String(item.pnl))}</span></div>`;
      }
      const value =
        typeof item.value === "number"
          ? item.value.toLocaleString("en-US")
          : escapeHtml(String(item.value ?? ""));
      return `<div class="tp-stat"><span class="tp-stat-label">${label}</span><span class="tp-stat-value">${value}</span></div>`;
    })
    .join("");
  return `<div class="tp-stats-card">${title}<div class="tp-stats-grid">${items}</div></div>`;
}

interface SourcesPayload {
  items?: Array<{ title?: unknown; url?: unknown; date?: unknown }>;
}

/**
 * Web sources, rendered as an explicitly-external attribution list. The visual
 * separation is the point: everything else in a reply is the user's own
 * reconstructed data, and this block is the one part that came from outside
 * and can be wrong. Links open in a new tab; only http(s) survives sanitizing.
 */
function renderSourcesCard(body: string): string {
  let parsed: SourcesPayload;
  try {
    parsed = JSON.parse(body);
    if (!parsed || !Array.isArray(parsed.items) || parsed.items.length === 0) {
      throw new Error("bad shape");
    }
  } catch {
    return `<pre class="tp-sources-fallback"><code>${escapeHtml(body)}</code></pre>`;
  }
  const items = parsed.items
    .map((item) => {
      const title = escapeHtml(
        typeof item.title === "string" && item.title ? item.title : "source",
      );
      const date =
        typeof item.date === "string" && item.date
          ? `<span class="tp-source-date">${escapeHtml(item.date)}</span>`
          : "";
      const url = typeof item.url === "string" ? item.url : "";
      const link = /^https?:\/\//i.test(url)
        ? `<a class="tp-source-link" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${title}</a>`
        : `<span class="tp-source-link">${title}</span>`;
      return `<li class="tp-source">${link}${date}</li>`;
    })
    .join("");
  return `<div class="tp-sources-card"><div class="tp-sources-title">From the web</div><ul class="tp-sources-list">${items}</ul></div>`;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Signed currency in prose: +$34,850 / -$69,765 / −$69,765 (unicode minus).
// Suffixed shorthand (+$99K) is left alone — reformatting it to two decimals
// would misstate precision the text never claimed.
const SIGNED_CURRENCY = /([+\-−])\$(\d[\d,]*(?:\.\d+)?)(?![\dKMkm])/g;

/**
 * Experiment-summary variant of renderAgentMarkdown: after the normal
 * pipeline, DECORATE signed currency amounts with the same caret spans the
 * chat's {{pnl:}} tokens get — green ▲ / red ▼, tabular numerals.
 *
 * Presentation-side on purpose: the hypothetical-hygiene guardrail (rightly)
 * forbids the MODEL from marking what-if outcomes with {{pnl:}} — those
 * tokens assert realized P/L. Here the whole document is explicitly a
 * hypothetical replay, so coloring its signed figures misleads no one, and
 * doing it in the renderer keeps the token semantics intact AND makes
 * already-stored summaries pretty retroactively. Code contexts and
 * {{calc:}} chips are left untouched (a chip keeps its own "assistant-
 * derived" styling).
 */
export function renderExperimentSummary(raw: string): string {
  const template = document.createElement("template");
  template.innerHTML = renderAgentMarkdown(raw);
  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    SIGNED_CURRENCY.lastIndex = 0;
    if (SIGNED_CURRENCY.test(n.nodeValue ?? "")) textNodes.push(n as Text);
  }
  for (const node of textNodes) {
    if (node.parentElement?.closest("pre, code, .tp-pnl, .tp-calc")) continue;
    const raw = node.nodeValue ?? "";
    SIGNED_CURRENCY.lastIndex = 0;
    const substituted = escapeHtml(raw).replace(
      SIGNED_CURRENCY,
      (_all, sign: string, digits: string) =>
        pnlSpan(`${sign === "−" ? "-" : sign}${digits}`),
    );
    if (substituted === escapeHtml(raw)) continue;
    const frag = document.createElement("template");
    frag.innerHTML = substituted;
    node.replaceWith(frag.content);
  }
  return template.innerHTML;
}
